import React, {useContext} from "react";
import {ICON_MAPPING, MARKERS_NAMES} from "./lib/icon-mapping";
import {ScatterMapProps} from "./types";
import _ from "lodash";
import {SUPPORTED_BUSINESSES} from "./lib/consts";

import DeckGL from "@deck.gl/react/typed";
import {BitmapLayer, IconLayer} from '@deck.gl/layers/typed';
import {TileLayer} from "@deck.gl/geo-layers/typed";
import {PickingInfo} from '@deck.gl/core/typed';
import CountryContext from "../../../CountryContext";
import { WorksitesPin } from "@feature/worksites/home/types";
import {DEFAULT_VIEWPORT} from "@feature/worksites/home/map/lib/consts";
import {getPinPropertiesKey} from "@feature/worksites/home/map/lib/utils";


const COPYRIGHT_LICENSE_STYLE = {
  position: 'absolute',
  right: 0,
  bottom: 0,
  backgroundColor: 'hsla(0,0%,100%,.5)',
  padding: '0 5px',
  font: '12px/20px Helvetica Neue,Arial,Helvetica,sans-serif'
};
const LINK_STYLE = {
  textDecoration: 'none',
  color: 'rgba(0,0,0,.75)',
  cursor: 'grab'
};

export const ScatterMap = ({
  id,
  data,
  onClick,
  onNavigation,
  initialViewport,
  draggable,
}: ScatterMapProps) => {

  // context
  const {country: {code}} = useContext(CountryContext);

  // utils
  const layers = [
    new TileLayer({
      id: `scatter-base-${id}`,
      data: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      maxRequests: 20,
      pickable: true,
      onViewportLoad: undefined,
      autoHighlight: false,
      highlightColor: [60, 60, 60, 40],
      minZoom: 0,
      maxZoom: 19,
      tileSize: 128,
      zoomOffset: -1,
      renderSubLayers: (props: any) => {
        const {west, south, east, north} = props.tile.bbox;
        const {data, ...otherProps} = props;
        return [
          new BitmapLayer(otherProps, {
            image: data,
            bounds: [west, south, east, north]
          }),
        ];
      }
    } as never),
    ...SUPPORTED_BUSINESSES
      .reduce((acc, curr) => [...acc, curr, `user-${curr}`], [] as string[])
      .map((category) => {
        const atlas = category.startsWith("user-")
          ? process.env.PUBLIC_URL + `/map/my-worksites-${category.replace("user-", "")}.svg`
          : process.env.PUBLIC_URL + `/map/${category}.svg`;
        return new IconLayer<WorksitesPin>({
          id: `icon-layer-${category}`,
          sizeScale: 50,
          data: data?.filter((p) => p.category === category) || [],
          pickable: true,
          iconAtlas: atlas,
          iconMapping: ICON_MAPPING,
          getPosition: (d) => [d.longitude, d.latitude],
          getIcon: ({count}) => getPinPropertiesKey(count),
          getSize: ({count}) => {
            const key = getPinPropertiesKey(count);
            const index = MARKERS_NAMES
              .findIndex((i) => i === key.replace("marker-", ""));
            return (((index + 1)/ MARKERS_NAMES.length) * (2 - 1)) + 1;
          },
        })
      })
  ];
  const onViewStateChange = _.debounce(
    ({viewState}) => onNavigation?.(viewState), 500
  );

  return (
    <DeckGL
      key={id}
      initialViewState={initialViewport ?? DEFAULT_VIEWPORT[code]}
      controller={{dragPan: draggable, scrollZoom: draggable}}
      style={{width: "100%", height: "100%", position: "absolute"}}
      layers={layers}
      getTooltip={({object}: PickingInfo) => object?.worksite_name}
      onClick={({object}: PickingInfo) => {
        if (!object?.worksite_id) return;
        onClick?.(object.worksite_id);
      }}
      onViewStateChange={onViewStateChange}
    >
      <div style={COPYRIGHT_LICENSE_STYLE as any}>
        {'© '}
        <a style={LINK_STYLE} href="https://www.openstreetmap.org/copyright" target="blank">
        OpenStreetMap contributors
        </a>
      </div>
    </DeckGL>
  );
}
